import { ReactNode, useEffect, useMemo } from 'react'
import { usePermissionStore, Role } from '@store/permissions'
import { useAuth } from './AuthProvider'
import { useContextStore } from '@store/context'
import { getAuthMode, isConsoleScopedInstance } from '@/config/runtime'
import { isTauriRuntimeAvailable } from '@/integrations/tauri/runtimeConfig'

interface PermissionProviderProps {
  children: ReactNode
  defaultRole?: Role
}

export function PermissionProvider({ children, defaultRole = Role.Viewer }: PermissionProviderProps) {
  const setRole = usePermissionStore((state) => state.setRole)
  const { organizations, isAuthenticated, userProfile } = useAuth()
  const org = useContextStore((state) => state.org)
  const consoleScopedInstance = isConsoleScopedInstance()
  // Align with AgentSpecStudioWriteMiddleware: console-scoped instances grant
  // Studio write:recipes to organization members. Local auth / Tauri desktop
  // follow the same authoring posture for members of the active organization.
  const localBuilderCapability =
    consoleScopedInstance ||
    getAuthMode() === 'local' ||
    (isTauriRuntimeAvailable() && !consoleScopedInstance)

  const resolvedRole = useMemo(() => {
    if (!isAuthenticated) {
      return Role.Viewer
    }

    const membership = organizations.find((item) => item.organization.id === org)
    const rawRole = (membership?.role || userProfile?.role || '').toLowerCase()
    const fallbackRole = localBuilderCapability ? Role.Builder : defaultRole

    switch (rawRole) {
      case 'admin':
      case 'owner':
      case 'orga-admin':
      case 'super-admin':
      case 'superadmin':
      case 'super_admin':
        return Role.Admin
      case 'builder':
        return Role.Builder
      case 'developer':
      case 'editor':
        return Role.Developer
      case 'operator':
      case 'member':
      case 'user':
        return localBuilderCapability ? Role.Builder : Role.Operator
      case 'viewer':
      case 'reader':
      case 'public':
        return Role.Viewer
      default:
        return fallbackRole
    }
  }, [defaultRole, isAuthenticated, localBuilderCapability, org, organizations, userProfile?.role])

  useEffect(() => {
    setRole(resolvedRole)
  }, [resolvedRole, setRole])

  return <>{children}</>
}
